import { getLiveClinic } from "./clinicLive";

// Use the live overlay so the audit always compares against the
// settings currently applied at runtime (not the static defaults).
const clinicNAP = new Proxy({} as ReturnType<typeof getLiveClinic>, {
  get: (_t, prop) => (getLiveClinic() as unknown as Record<string, unknown>)[prop as string],
});

export type NapField = "name" | "street" | "postalCode" | "phone" | "hours";

export interface NapCheckItem {
  field: NapField;
  label: string;
  expected: string;
  ok: boolean;
  occurrences: number;
  /** Conflicting values found in the DOM (e.g. an old phone number left behind). */
  conflicts: string[];
}

export interface NapAuditResult {
  ok: boolean;
  url: string;
  checkedAt: number;
  items: NapCheckItem[];
  /** Cross-source verification: visible DOM vs JSON-LD schema. */
  schemaMatchesDom: boolean;
  schemaIssues: string[];
  /** Focused JSON-LD ↔ header/footer NAP widget validation. */
  schemaVsWidgets: SchemaWidgetValidation;
}

export interface SchemaWidgetFieldCheck {
  field: NapField;
  label: string;
  /** Value rendered inside the on-site header/footer widget. */
  rendered: string | null;
  /** Equivalent value found inside the page's clinic JSON-LD node. */
  schema: string | null;
  ok: boolean;
  /** Where the rendered value came from. */
  sources: Array<"header" | "footer">;
}

export interface SchemaWidgetValidation {
  /** True when every header/footer field aligns with the JSON-LD clinic node. */
  ok: boolean;
  /** True when at least one header/footer NAP widget was found on the page. */
  hasWidget: boolean;
  /** True when a clinic-typed JSON-LD node was found. */
  hasSchema: boolean;
  fields: SchemaWidgetFieldCheck[];
  /** Plain-English drift descriptions for the warning banner. */
  drift: string[];
}

/** Strip element from a clone before reading text (e.g. <script>, <style>). */
const cleanText = (root: HTMLElement): string => {
  const clone = root.cloneNode(true) as HTMLElement;
  clone.querySelectorAll("script, style, noscript, template").forEach((n) => n.remove());
  return (clone.textContent || "").replace(/\s+/g, " ").trim();
};

/** Conservative scan for any phone-shaped string so we can spot drift. */
const PHONE_RE = /\+?\d[\d\s().-]{7,}\d/g;

const normalisePhone = (raw: string) => raw.replace(/[^\d+]/g, "");

/**
 * Convert a phone string to its canonical digits-only E.164-ish form so a
 * locally-formatted number ("020 3370 3503") compares equal to its
 * international counterpart ("+44 20 3370 3503"). Defaults to UK (+44) when
 * a leading "0" is detected without an explicit country code.
 */
const canonicalPhoneDigits = (raw: string, defaultCc = "44"): string => {
  const trimmed = raw.replace(/[^\d+]/g, "");
  if (trimmed.startsWith("+")) return trimmed.slice(1);
  if (trimmed.startsWith("00")) return trimmed.slice(2);
  if (trimmed.startsWith("0")) return defaultCc + trimmed.slice(1);
  return trimmed;
};

const collectAllJsonLd = (): unknown[] => {
  const out: unknown[] = [];
  document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
    try {
      const parsed = JSON.parse(el.textContent || "");
      if (Array.isArray(parsed)) out.push(...parsed);
      else out.push(parsed);
    } catch {
      /* ignore malformed */
    }
  });
  return out;
};

/** Walk a JSON-LD value (incl. @graph) collecting every node with a @type. */
const flattenSchemaNodes = (input: unknown): Record<string, unknown>[] => {
  const nodes: Record<string, unknown>[] = [];
  const walk = (v: unknown) => {
    if (!v || typeof v !== "object") return;
    if (Array.isArray(v)) {
      v.forEach(walk);
      return;
    }
    const obj = v as Record<string, unknown>;
    if ("@type" in obj) nodes.push(obj);
    if (Array.isArray(obj["@graph"])) (obj["@graph"] as unknown[]).forEach(walk);
    Object.values(obj).forEach((child) => {
      if (child && typeof child === "object") walk(child);
    });
  };
  walk(input);
  return nodes;
};

const findClinicSchema = (): Record<string, unknown> | null => {
  const nodes = flattenSchemaNodes(collectAllJsonLd());
  return (
    nodes.find((n) => {
      const t = n["@type"];
      const types = Array.isArray(t) ? t : typeof t === "string" ? [t] : [];
      return types.some((x) =>
        ["LocalBusiness", "MedicalBusiness", "MedicalOrganization", "MedicalClinic"].includes(x),
      );
    }) || null
  );
};

/**
 * Run a NAP audit against the live DOM and the page's JSON-LD schema.
 * Returns a structured result you can render in a debug panel or log to console.
 */
export const runNapAudit = (root: HTMLElement = document.body): NapAuditResult => {
  const text = cleanText(root);
  const items: NapCheckItem[] = [];

  const expectedName = clinicNAP.name;
  const nameOccurrences = (text.match(new RegExp(escapeRegExp(expectedName), "g")) || []).length;
  items.push({
    field: "name",
    label: "Clinic name",
    expected: expectedName,
    ok: nameOccurrences > 0,
    occurrences: nameOccurrences,
    conflicts: [],
  });

  const expectedStreet = clinicNAP.address.street;
  const streetOccurrences = (text.match(new RegExp(escapeRegExp(expectedStreet), "g")) || []).length;
  items.push({
    field: "street",
    label: "Street address",
    expected: expectedStreet,
    ok: streetOccurrences > 0,
    occurrences: streetOccurrences,
    conflicts: [],
  });

  const expectedPostcode = clinicNAP.address.postalCode;
  const postcodeOccurrences =
    (text.match(new RegExp(escapeRegExp(expectedPostcode), "g")) || []).length;
  items.push({
    field: "postalCode",
    label: "Postcode",
    expected: expectedPostcode,
    ok: postcodeOccurrences > 0,
    occurrences: postcodeOccurrences,
    conflicts: [],
  });

  // Phone — match by digits-only so spacing variations don't cause false fails,
  // but flag any phone-shaped string whose digits don't match the canonical one.
  // The clinic's WhatsApp number is an allowed secondary contact.
  const expectedDigits = canonicalPhoneDigits(clinicNAP.phone.international);
  const whatsappDigits = canonicalPhoneDigits(clinicNAP.whatsapp.number);
  const allowedDigits = new Set([expectedDigits, whatsappDigits]);
  const phoneMatches = text.match(PHONE_RE) || [];
  const normalised = phoneMatches.map((p) => canonicalPhoneDigits(p)).filter((d) => d.length >= 8);
  const phoneOccurrences = normalised.filter((d) => d === expectedDigits).length;
  const phoneConflicts = Array.from(
    new Set(
      phoneMatches.filter((p) => {
        const d = canonicalPhoneDigits(p);
        return d.length >= 8 && !allowedDigits.has(d);
      }),
    ),
  );
  items.push({
    field: "phone",
    label: "Phone number",
    expected: clinicNAP.phone.display,
    ok: phoneOccurrences > 0 && phoneConflicts.length === 0,
    occurrences: phoneOccurrences,
    conflicts: phoneConflicts,
  });

  // Hours — at least one configured row should be visible verbatim.
  const expectedHours = clinicNAP.hours[0]?.time ?? "";
  const hoursOccurrences =
    expectedHours && text.includes(expectedHours) ? 1 : 0;
  items.push({
    field: "hours",
    label: "Opening hours",
    expected: expectedHours,
    ok: hoursOccurrences > 0,
    occurrences: hoursOccurrences,
    conflicts: [],
  });

  // Schema cross-check
  const schemaIssues: string[] = [];
  const clinicSchema = findClinicSchema();
  if (!clinicSchema) {
    schemaIssues.push("No LocalBusiness/MedicalOrganization JSON-LD found on this page.");
  } else {
    const addr = clinicSchema.address as Record<string, unknown> | undefined;
    if (clinicSchema.name !== expectedName) {
      schemaIssues.push(`Schema name "${String(clinicSchema.name)}" ≠ "${expectedName}"`);
    }
    if (addr?.streetAddress !== expectedStreet) {
      schemaIssues.push(
        `Schema streetAddress "${String(addr?.streetAddress)}" ≠ "${expectedStreet}"`,
      );
    }
    if (addr?.postalCode !== expectedPostcode) {
      schemaIssues.push(
        `Schema postalCode "${String(addr?.postalCode)}" ≠ "${expectedPostcode}"`,
      );
    }
    const schemaPhone = String(clinicSchema.telephone ?? "");
    if (canonicalPhoneDigits(schemaPhone) !== expectedDigits) {
      schemaIssues.push(`Schema telephone "${schemaPhone}" ≠ "${clinicNAP.phone.international}"`);
    }
  }

  // Focused JSON-LD ↔ header/footer widget validation
  const schemaVsWidgets = validateSchemaAgainstWidgets(clinicSchema);

  const ok =
    items.every((i) => i.ok) &&
    schemaIssues.length === 0 &&
    schemaVsWidgets.ok;

  return {
    ok,
    url: window.location.pathname + window.location.search,
    checkedAt: Date.now(),
    items,
    schemaMatchesDom: schemaIssues.length === 0,
    schemaIssues,
    schemaVsWidgets,
  };
};

/** Pull the rendered NAP fields from a header/footer ClinicNAP widget. */
const readWidget = (
  el: HTMLElement,
): Partial<Record<NapField, string>> => {
  const get = (prop: string): string | undefined => {
    const node = el.querySelector(`[itemprop="${prop}"]`);
    return node?.textContent?.replace(/\s+/g, " ").trim() || undefined;
  };
  const name = get("name") ?? el.querySelector('meta[itemprop="name"]')?.getAttribute("content") ?? undefined;
  const street = get("streetAddress");
  const postalCode = get("postalCode");
  const phone = get("telephone");
  // Hours: prefer the visible row, fall back to the schema-formatted meta tag.
  const hoursRow = el.querySelector("ul li")?.textContent?.replace(/\s+/g, " ").trim();
  return {
    name,
    street,
    postalCode,
    phone,
    ...(hoursRow ? { hours: hoursRow } : {}),
  };
};

const compareValues = (a?: string | null, b?: string | null) => {
  if (!a || !b) return false;
  return a.replace(/\s+/g, " ").trim().toLowerCase() ===
    b.replace(/\s+/g, " ").trim().toLowerCase();
};

/**
 * Validate that the page's clinic JSON-LD node matches the values rendered
 * inside the header and footer NAP widgets. This is the strict check the
 * marketing team cares about: the structured data Google reads MUST equal
 * what visitors see in the chrome of every page.
 */
export const validateSchemaAgainstWidgets = (
  clinicSchema: Record<string, unknown> | null = findClinicSchema(),
): SchemaWidgetValidation => {
  const widgets = Array.from(
    document.querySelectorAll<HTMLElement>("[data-nap-source]"),
  );

  const merged: Partial<Record<NapField, { value: string; sources: Array<"header" | "footer"> }>> = {};
  for (const w of widgets) {
    const source = w.getAttribute("data-nap-source") as "header" | "footer" | null;
    if (source !== "header" && source !== "footer") continue;
    const fields = readWidget(w);
    (Object.keys(fields) as NapField[]).forEach((f) => {
      const v = fields[f];
      if (!v) return;
      if (!merged[f]) merged[f] = { value: v, sources: [source] };
      else if (!merged[f]!.sources.includes(source)) merged[f]!.sources.push(source);
    });
  }

  const addr = (clinicSchema?.address as Record<string, unknown> | undefined) ?? undefined;
  const expectedHoursRow = `${clinicNAP.hours[0]?.days} ${clinicNAP.hours[0]?.time}`;

  const schemaValues: Record<NapField, string | null> = {
    name: clinicSchema ? String(clinicSchema.name ?? "") || null : null,
    street: addr?.streetAddress ? String(addr.streetAddress) : null,
    postalCode: addr?.postalCode ? String(addr.postalCode) : null,
    phone: clinicSchema?.telephone ? String(clinicSchema.telephone) : null,
    hours: expectedHoursRow,
  };

  const fieldDefs: Array<{ field: NapField; label: string }> = [
    { field: "name", label: "Clinic name" },
    { field: "street", label: "Street address" },
    { field: "postalCode", label: "Postcode" },
    { field: "phone", label: "Phone number" },
    { field: "hours", label: "Opening hours" },
  ];

  const fields: SchemaWidgetFieldCheck[] = fieldDefs.map(({ field, label }) => {
    const renderedEntry = merged[field];
    const rendered = renderedEntry?.value ?? null;
    const schema = schemaValues[field];

    let ok = true;
    if (rendered && schema) {
      if (field === "phone") {
        ok = canonicalPhoneDigits(rendered) === canonicalPhoneDigits(schema);
      } else if (field === "hours") {
        // Schema doesn't store the friendly row text; instead, the rendered
        // first-row text must contain the canonical hours string.
        ok = rendered.toLowerCase().includes((clinicNAP.hours[0]?.time ?? "").toLowerCase());
      } else {
        ok = compareValues(rendered, schema);
      }
    } else if (rendered && !schema) {
      ok = false;
    }

    return {
      field,
      label,
      rendered,
      schema,
      ok,
      sources: renderedEntry?.sources ?? [],
    };
  });

  const hasWidget = widgets.length > 0;
  const hasSchema = !!clinicSchema;
  const drift: string[] = [];
  if (hasWidget && !hasSchema) {
    drift.push("Page renders the NAP widget but has no LocalBusiness JSON-LD.");
  }
  fields.forEach((f) => {
    if (f.ok || !f.rendered) return;
    drift.push(
      `${f.label}: header/footer shows "${f.rendered}" but JSON-LD has "${f.schema ?? "—"}".`,
    );
  });

  return {
    ok: drift.length === 0 && fields.every((f) => f.ok),
    hasWidget,
    hasSchema,
    fields,
    drift,
  };
};

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
